/*
 * SpendMapping.js — Phase 2 Revenue & Spend Mapping UI
 *
 * Full CRUD table for per-client ad-spend entries with:
 *   • Category chips (6 seeded defaults + ad-hoc via free text)
 *   • Start/end date range per entry
 *   • Overlap-preview modal on save (warns when a new entry overlaps
 *     existing entries in the SAME category)
 *   • Currency locked to the client's currency (design decision 2a)
 *   • Fiscal-week selector at the top (design decision 4c)
 *   • Rolling 7/30/90-day rolling-total preview for the last-year context
 *
 * Backed by /api/clients/{id}/spend-entries + spend-summary endpoints.
 * Visible to super_admin AND admin_user; write-guarded server-side.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Plus, PencilSimple, Trash, Warning, X as XIcon, CurrencyCircleDollar, CalendarBlank } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CATEGORY_LABELS = {
  perf: "Performance",
  branding: "Branding",
  meta: "Meta",
  google: "Google",
  snapchat: "Snapchat",
  zoko: "Zoko",
};

const FISCAL_WEEK_OPTIONS = [
  { value: "mon", label: "Monday → Sunday" },
  { value: "sun", label: "Sunday → Saturday" },
  { value: "sat", label: "Saturday → Friday" },
];

const fmtMoney = (amt, ccy) => {
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency: ccy || "INR", maximumFractionDigits: 0,
    }).format(Number(amt) || 0);
  } catch {
    return `${ccy || ""} ${Math.round(Number(amt) || 0).toLocaleString("en-IN")}`;
  }
};

const humanCategory = (cat) => DEFAULT_CATEGORY_LABELS[cat] || (cat ? cat.charAt(0).toUpperCase() + cat.slice(1) : "");

const emptyForm = () => ({
  category: "meta",
  amount: "",
  period_start: "",
  period_end: "",
  notes: "",
});

const SpendMapping = ({ clientId, clientCurrency = "INR", fiscalWeekStart = "mon", onFiscalWeekChanged }) => {
  const [entries, setEntries] = useState([]);
  const [categories, setCategories] = useState({ defaults: [], in_use: [], all: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState(null); // null | [] | [entry, entry]
  const [error, setError] = useState(null);
  const [fiscalWeek, setFiscalWeek] = useState(fiscalWeekStart || "mon");
  const [customCategory, setCustomCategory] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [entriesRes, catsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}/spend-entries`),
        axios.get(`${API}/clients/${clientId}/spend-categories`),
      ]);
      setEntries(entriesRes.data?.entries || []);
      setCategories(catsRes.data || { defaults: [], in_use: [], all: [] });
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load spend entries");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setConflicts(null);
    setCustomCategory("");
    setShowForm(true);
  };

  const openEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      category: entry.category,
      amount: String(entry.amount ?? ""),
      period_start: entry.period_start,
      period_end: entry.period_end,
      notes: entry.notes || "",
    });
    setConflicts(null);
    setCustomCategory("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm());
    setConflicts(null);
    setCustomCategory("");
    setError(null);
  };

  const validForm = form.category && form.amount && form.period_start && form.period_end && Number(form.amount) >= 0 && form.period_start <= form.period_end;

  const checkAndSave = async (e) => {
    e?.preventDefault?.();
    if (!validForm) return;
    setSaving(true);
    setError(null);
    try {
      const payload = {
        category: form.category.trim().toLowerCase(),
        amount: Number(form.amount),
        period_start: form.period_start,
        period_end: form.period_end,
        notes: form.notes || undefined,
      };
      // If creating (not editing) and conflicts not confirmed yet, check first
      if (!editingId && conflicts === null) {
        const overlapRes = await axios.post(
          `${API}/clients/${clientId}/spend-entries/preview-overlaps`,
          payload,
        );
        if ((overlapRes.data?.conflicts || []).length > 0) {
          setConflicts(overlapRes.data.conflicts);
          setSaving(false);
          return;
        }
        setConflicts([]);
      }
      if (editingId) {
        await axios.patch(`${API}/clients/${clientId}/spend-entries/${editingId}`, payload);
      } else {
        await axios.post(`${API}/clients/${clientId}/spend-entries`, payload);
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm("Delete this spend entry? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/clients/${clientId}/spend-entries/${id}`);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Delete failed");
    }
  };

  const addCustomCategory = () => {
    const v = customCategory.trim().toLowerCase();
    if (!v) return;
    setForm(f => ({ ...f, category: v }));
    setCustomCategory("");
  };

  const changeFiscalWeek = async (v) => {
    setFiscalWeek(v);
    try {
      await axios.patch(`${API}/clients/${clientId}/fiscal-week`, { fiscal_week_start: v });
      onFiscalWeekChanged?.(v);
    } catch (e) {
      alert(e.response?.data?.detail || "Failed to update fiscal week");
    }
  };

  // Totals summary for the current-year view
  const yearTotal = useMemo(() => entries.reduce((s, e) => s + (Number(e.amount) || 0), 0), [entries]);

  return (
    <div className="md:col-span-2 space-y-4" data-testid="spend-mapping-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2" style={{ fontFamily: "Varela Round, sans-serif" }}>
            <CurrencyCircleDollar size={16} className="text-[#FFEB00]" /> Ad Spend & Revenue Mapping
          </h3>
          <p className="text-[11px] text-white/50 mt-1">
            Enter ad-spend by category and date-range. The dashboard automatically pro-rates any
            entry that overlaps your selected report window (e.g. a ₹300k Jun 1-30 entry contributes
            ₹100k to a Jun 1-10 report). All amounts are in the client&apos;s currency ({clientCurrency}).
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-1.5 text-[11px] px-3 py-1.5 rounded bg-[#FFEB00] text-black font-semibold hover:brightness-110 transition"
          data-testid="spend-add-btn"
        >
          <Plus size={14} weight="bold" /> Add spend entry
        </button>
      </div>

      {/* Fiscal week + summary row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10" data-testid="spend-fiscal-week">
          <label className="text-[10px] text-white/50 uppercase tracking-wider flex items-center gap-1.5 mb-1.5">
            <CalendarBlank size={12} /> Fiscal week starts on
          </label>
          <select
            value={fiscalWeek}
            onChange={(e) => changeFiscalWeek(e.target.value)}
            className="w-full bg-[#0B0B0F] border border-white/10 text-[12px] text-white px-2 py-1.5 rounded focus:outline-none focus:border-[#FFEB00]/60"
            data-testid="spend-fiscal-week-select"
          >
            {FISCAL_WEEK_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
          <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1.5">Total entries</p>
          <p className="text-lg font-semibold text-white" data-testid="spend-total-entries">{entries.length}</p>
        </div>
        <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10">
          <p className="text-[10px] text-white/50 uppercase tracking-wider mb-1.5">Sum of all entries</p>
          <p className="text-lg font-semibold text-[#FFEB00]" data-testid="spend-year-total">{fmtMoney(yearTotal, clientCurrency)}</p>
          <p className="text-[10px] text-white/40 mt-0.5 italic">Raw sum · not pro-rated</p>
        </div>
      </div>

      {/* Entries table */}
      <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="spend-entries-table">
        <table className="w-full text-[12px]">
          <thead className="bg-white/[0.03] border-b border-white/10">
            <tr>
              <th className="text-left py-2 px-3 text-white/60 font-medium uppercase tracking-wider text-[10px]">Category</th>
              <th className="text-left py-2 px-3 text-white/60 font-medium uppercase tracking-wider text-[10px]">Period</th>
              <th className="text-right py-2 px-3 text-white/60 font-medium uppercase tracking-wider text-[10px]">Amount</th>
              <th className="text-left py-2 px-3 text-white/60 font-medium uppercase tracking-wider text-[10px]">Notes</th>
              <th className="text-right py-2 px-3 text-white/60 font-medium uppercase tracking-wider text-[10px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="py-6 text-center text-white/50 text-[12px]">Loading…</td></tr>
            )}
            {!loading && entries.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-white/50 text-[12px]" data-testid="spend-empty-state">
                No spend entries yet. Click <strong>Add spend entry</strong> to record your first month of ad-spend.
              </td></tr>
            )}
            {!loading && entries.map((e) => (
              <tr key={e.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.02]" data-testid={`spend-row-${e.id}`}>
                <td className="py-2 px-3">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10 text-white/80">{humanCategory(e.category)}</span>
                </td>
                <td className="py-2 px-3 text-white/80">{e.period_start} → {e.period_end}</td>
                <td className="py-2 px-3 text-right text-white/90 font-medium">{fmtMoney(e.amount, e.currency || clientCurrency)}</td>
                <td className="py-2 px-3 text-white/50 text-[11px] max-w-[200px] truncate">{e.notes || "—"}</td>
                <td className="py-2 px-3 text-right">
                  <button type="button" onClick={() => openEdit(e)} className="text-white/60 hover:text-white p-1" data-testid={`spend-edit-${e.id}`}><PencilSimple size={13} /></button>
                  <button type="button" onClick={() => removeEntry(e.id)} className="text-red-400/70 hover:text-red-400 p-1 ml-1" data-testid={`spend-delete-${e.id}`}><Trash size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 bg-black/60 backdrop-blur-sm" data-testid="spend-form-modal">
          <div className="bg-[#0B0B0F] border border-white/10 rounded-xl w-full max-w-lg p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-white">{editingId ? "Edit spend entry" : "Add spend entry"}</h3>
              <button type="button" onClick={closeForm} className="text-white/60 hover:text-white" data-testid="spend-form-close"><XIcon size={16} /></button>
            </div>

            {/* Category */}
            <div>
              <label className="text-[10px] text-white/50 uppercase tracking-wider">Category</label>
              <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="spend-category-chips">
                {categories.all.map(cat => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, category: cat }))}
                    className={`text-[11px] px-2.5 py-1 rounded border transition ${
                      form.category === cat
                        ? "bg-[#FFEB00] text-black border-[#FFEB00] font-semibold"
                        : "bg-white/[0.03] text-white/70 border-white/10 hover:bg-white/[0.06]"
                    }`}
                    data-testid={`spend-cat-${cat}`}
                  >
                    {humanCategory(cat)}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="Add custom category (e.g. TikTok, Radio)"
                  className="flex-1 bg-white/[0.03] border border-white/10 text-[12px] text-white px-2 py-1 rounded focus:outline-none focus:border-[#FFEB00]/60"
                  data-testid="spend-custom-category-input"
                />
                <button type="button" onClick={addCustomCategory} className="text-[11px] px-2.5 py-1 rounded border border-white/15 text-white/80 hover:bg-white/[0.06]" data-testid="spend-custom-category-add">
                  Use
                </button>
              </div>
              {form.category && (
                <p className="text-[10px] text-white/50 mt-1.5">Selected: <strong className="text-white/80">{humanCategory(form.category)}</strong></p>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="text-[10px] text-white/50 uppercase tracking-wider">Amount ({clientCurrency})</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm(f => ({ ...f, amount: e.target.value }))}
                className="w-full mt-1.5 bg-white/[0.03] border border-white/10 text-white px-2 py-1.5 rounded focus:outline-none focus:border-[#FFEB00]/60"
                data-testid="spend-amount-input"
                placeholder="e.g. 300000"
              />
            </div>

            {/* Period */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] text-white/50 uppercase tracking-wider">Period start</label>
                <input
                  type="date"
                  value={form.period_start}
                  onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))}
                  className="w-full mt-1.5 bg-white/[0.03] border border-white/10 text-white px-2 py-1.5 rounded [color-scheme:dark] focus:outline-none focus:border-[#FFEB00]/60"
                  data-testid="spend-period-start"
                />
              </div>
              <div>
                <label className="text-[10px] text-white/50 uppercase tracking-wider">Period end</label>
                <input
                  type="date"
                  value={form.period_end}
                  min={form.period_start || undefined}
                  onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))}
                  className="w-full mt-1.5 bg-white/[0.03] border border-white/10 text-white px-2 py-1.5 rounded [color-scheme:dark] focus:outline-none focus:border-[#FFEB00]/60"
                  data-testid="spend-period-end"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-[10px] text-white/50 uppercase tracking-wider">Notes (optional)</label>
              <input
                type="text"
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full mt-1.5 bg-white/[0.03] border border-white/10 text-white px-2 py-1.5 rounded focus:outline-none focus:border-[#FFEB00]/60"
                data-testid="spend-notes-input"
                placeholder="e.g. Diwali burst campaign"
              />
            </div>

            {/* Conflict warning */}
            {conflicts && conflicts.length > 0 && (
              <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/40 space-y-2" data-testid="spend-overlap-warning">
                <div className="flex items-center gap-2 text-yellow-300 text-[12px] font-semibold">
                  <Warning size={14} weight="fill" /> Overlap detected
                </div>
                <p className="text-[11px] text-white/70">
                  This new entry overlaps with {conflicts.length} existing entry in the same category. Saving will keep BOTH — the aggregator will double-count for the overlap window. Please confirm.
                </p>
                <ul className="text-[10px] text-white/60 space-y-0.5">
                  {conflicts.slice(0, 3).map(c => (
                    <li key={c.id}>• {c.period_start} → {c.period_end} · {fmtMoney(c.amount, c.currency)}</li>
                  ))}
                </ul>
              </div>
            )}

            {error && <p className="text-[11px] text-red-400" data-testid="spend-form-error">{error}</p>}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/10">
              <button type="button" onClick={closeForm} className="text-[12px] px-3 py-1.5 rounded border border-white/15 text-white/80 hover:bg-white/[0.06]" data-testid="spend-form-cancel">
                Cancel
              </button>
              <button
                type="button"
                disabled={!validForm || saving}
                onClick={checkAndSave}
                className="text-[12px] px-3 py-1.5 rounded bg-[#FFEB00] text-black font-semibold hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="spend-form-save"
              >
                {saving ? "Saving…" : conflicts && conflicts.length > 0 ? "Save anyway" : editingId ? "Save changes" : "Add entry"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpendMapping;
